import React, { useState } from 'react';
import {
  Badge, Button, Card, EmptyState, Input, Modal, PageHead, SkeletonRows,
  Textarea, timeAgo, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { notificationApi } from '../api';
import { useToast } from '../context/ToastContext';

const TYPE_ICONS = {
  order: '🛒', payment: '₹', authorization: '⛨', verification: '✓', contract: '📄',
  claim: '⚠', shipment: '🚚', insurance: '🛡', finance: '🏦', inventory: '⛁',
  account: '⚇', promotion: '★', general: '🔔',
};

const ROLES = ['buyer', 'manufacturer', 'distributor', 'supplier', 'logistics', 'insurance', 'finance'];

const Notifications = () => {
  const toast = useToast();
  const list = useList(notificationApi.list, { type: '' });

  const [compose, setCompose] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', roles: [], channels: ['in_app', 'push'] });
  const [busy, setBusy] = useState(false);

  const toggle = (key, value) =>
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
    }));

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error('Add a title and a message');

    setBusy(true);
    try {
      const res = await notificationApi.broadcast(form);
      toast.success(res.message);
      setCompose(false);
      setForm({ title: '', body: '', roles: [], channels: ['in_app', 'push'] });
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const markAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      toast.success('All caught up');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <PageHead
        title="Notifications"
        subtitle="Your alert feed, plus broadcasts to any role on the network."
        actions={
          <>
            <Button variant="ghost" onClick={markAllRead}>Mark all read</Button>
            <Button icon="📣" onClick={() => setCompose(true)}>Broadcast</Button>
          </>
        }
      />

      <div className="filters">
        <select className="select" style={{ width: 200 }} value={list.filters.type} onChange={e => list.setFilter('type', e.target.value)}>
          <option value="">All notification types</option>
          {Object.keys(TYPE_ICONS).map(t => <option key={t} value={t}>{titleCase(t)}</option>)}
        </select>
      </div>

      {list.loading ? (
        <SkeletonRows rows={6} cols={2} />
      ) : list.rows.length === 0 ? (
        <Card><EmptyState icon="🔔" title="No notifications" message="Platform events you should know about will land here." /></Card>
      ) : (
        <div className="col stagger" style={{ gap: 9 }}>
          {list.rows.map(item => (
            <div
              key={item._id}
              className="card"
              style={{
                borderLeft: `3px solid ${item.isRead ? 'var(--line)' : 'var(--green-500)'}`,
                cursor: item.isRead ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (!item.isRead) notificationApi.markRead(item._id).then(() => list.reload()).catch(() => {});
              }}
            >
              <div className="card-body row" style={{ alignItems: 'flex-start', gap: 13 }}>
                <div
                  style={{
                    width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                    background: item.isRead ? 'var(--line-2)' : 'var(--green-50)',
                    display: 'grid', placeItems: 'center', fontSize: 17,
                  }}
                >
                  {TYPE_ICONS[item.type] || '🔔'}
                </div>
                <div className="grow">
                  <div className="row-between">
                    <div style={{ fontWeight: item.isRead ? 500 : 700 }}>{item.title}</div>
                    <span className="text-muted text-sm">{timeAgo(item.createdAt)}</span>
                  </div>
                  <div className="text-muted text-sm mt-1">{item.body}</div>
                  <div className="row mt-1" style={{ gap: 6 }}>
                    <Badge status={item.type} tone="brand" dot={false}>{titleCase(item.type)}</Badge>
                    {!item.isRead && <Badge status="pending" dot={false}>New</Badge>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={compose}
        title="Broadcast a notification"
        subtitle="Reaches every matching account across the network"
        onClose={() => setCompose(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCompose(false)}>Cancel</Button>
            <Button loading={busy} onClick={send}>Send broadcast</Button>
          </>
        }
      >
        <Input label="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Scheduled maintenance on Sunday" />
        <Textarea label="Message" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={3} placeholder="The platform will be unavailable from 2–4 AM IST for upgrades." />

        <div className="field">
          <label>Send to roles</label>
          <div className="row wrap" style={{ gap: 7 }}>
            {ROLES.map(role => (
              <button
                key={role}
                type="button"
                className={`btn btn-sm ${form.roles.includes(role) ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => toggle('roles', role)}
              >
                {titleCase(role)}
              </button>
            ))}
          </div>
          <div className="hint">Select none to send to every account on the platform.</div>
        </div>

        <div className="field">
          <label>Channels</label>
          <div className="row wrap" style={{ gap: 7 }}>
            {['in_app', 'push', 'whatsapp', 'email', 'sms'].map(channel => (
              <button
                key={channel}
                type="button"
                className={`btn btn-sm ${form.channels.includes(channel) ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => toggle('channels', channel)}
              >
                {titleCase(channel)}
              </button>
            ))}
          </div>
          <div className="hint">Channels that are disabled or unconfigured in Settings are skipped silently.</div>
        </div>
      </Modal>
    </>
  );
};

export default Notifications;
